import { useEffect, useState, type FormEvent } from 'react'
import { supabase } from '../lib/supabase'
import { BrandMark } from './BrandMark'

export function LoginScreen() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [resetMode, setResetMode] = useState(false)
  const [resetSent, setResetSent] = useState(false)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)

    if (resetMode) {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/?mode=recovery`,
      })

      if (resetError) setError(resetError.message)
      else setResetSent(true)
      setBusy(false)
      return
    }

    const { error: signInError } = await supabase.auth.signInWithPassword({ email, password })
    if (signInError) setError(signInError.message)
    setBusy(false)
  }

  function openResetMode() {
    setResetMode(true)
    setResetSent(false)
    setError(null)
    setPassword('')
  }

  function returnToSignIn() {
    setResetMode(false)
    setResetSent(false)
    setError(null)
  }

  return (
    <main className="login-page">
      <section className="login-panel">
        <BrandMark />
        <div className="login-panel__copy">
          <span className="eyebrow">Venue operations, without the paperwork fog</span>
          <h1>Compliance that follows the trading day.</h1>
          <p>
            Manage live checks, weekly work, staff PIN profiles and area-specific kiosks from one calm control room.
          </p>
        </div>
        <div className="login-panel__features" aria-hidden="true">
          <span>Trading-date aware</span>
          <span>Area routed</span>
          <span>Audit ready</span>
        </div>
      </section>

      <section className="login-card" aria-label={resetMode ? 'Reset password' : 'Manager sign in'}>
        <div>
          <span className="eyebrow">Manager access</span>
          <h2>{resetMode ? 'Reset your VenTuhi password' : 'Sign in to Compliance'}</h2>
          <p>
            {resetMode
              ? 'We will email a secure recovery link. Your new password will work for both Security and Compliance.'
              : 'Use your VenTuhi management account. Routine staff use the paired kiosk instead.'}
          </p>
        </div>

        {resetSent ? (
          <div className="form-stack">
            <div className="message message--success">
              Recovery email sent. Check your inbox and junk folder, then open the link on this device.
            </div>
            <button className="button button--ghost" type="button" onClick={returnToSignIn}>
              Return to sign in
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="form-stack">
            <label>
              Email address
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                required
              />
            </label>
            {!resetMode && (
              <>
                <label>
                  Password
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    autoComplete="current-password"
                    required
                  />
                </label>
                <button className="text-link" type="button" onClick={() => setShowPassword((visible) => !visible)}>
                  {showPassword ? 'Hide password' : 'Show password'}
                </button>
              </>
            )}
            {error && <div className="message message--error">{error}</div>}
            <button className="button button--primary button--large" disabled={busy}>
              {busy
                ? resetMode ? 'Sending recovery email…' : 'Opening workspace…'
                : resetMode ? 'Send recovery email' : 'Open Compliance'}
            </button>
            <button className="text-link" type="button" onClick={resetMode ? returnToSignIn : openResetMode}>
              {resetMode ? 'Return to sign in' : 'Forgot password?'}
            </button>
          </form>
        )}

        <a className="text-link" href="/?mode=kiosk">
          Set up or open a Compliance kiosk
        </a>
      </section>
    </main>
  )
}

export function PasswordRecoveryScreen() {
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [mfaCode, setMfaCode] = useState('')
  const [mfaRequired, setMfaRequired] = useState(false)
  const [showPasswords, setShowPasswords] = useState(false)
  const [ready, setReady] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [complete, setComplete] = useState(false)

  useEffect(() => {
    let active = true

    async function prepareRecoverySession() {
      const { data: sessionData } = await supabase.auth.getSession()
      if (!active || !sessionData.session) return

      const { data: assurance, error: assuranceError } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
      if (!active) return

      if (assuranceError) {
        setError(assuranceError.message)
        return
      }

      setMfaRequired(assurance?.nextLevel === 'aal2' && assurance.currentLevel !== 'aal2')
      setReady(true)
      setError(null)
    }

    void prepareRecoverySession()

    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (!active || (!session && event !== 'PASSWORD_RECOVERY')) return
      void prepareRecoverySession()
    })

    const timeout = window.setTimeout(() => {
      if (active) {
        setError((current) => current ?? 'This recovery link is invalid or has expired. Request a new link from the sign-in screen.')
      }
    }, 7000)

    return () => {
      active = false
      window.clearTimeout(timeout)
      listener.subscription.unsubscribe()
    }
  }, [])

  async function verifyMfaIfRequired() {
    const { data: assurance, error: assuranceError } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
    if (assuranceError) throw assuranceError
    if (assurance?.currentLevel === 'aal2' || assurance?.nextLevel !== 'aal2') return

    if (!/^\d{6}$/.test(mfaCode.trim())) {
      throw new Error('Enter the six-digit code from your authenticator app.')
    }

    const { data: factors, error: factorsError } = await supabase.auth.mfa.listFactors()
    if (factorsError) throw factorsError

    const factor =
      factors?.totp?.find((item) => item.status === 'verified') ??
      factors?.phone?.find((item) => item.status === 'verified')

    if (!factor) {
      throw new Error('No verified MFA factor is available for this account. An administrator will need to recover the account.')
    }

    const { error: verificationError } = await supabase.auth.mfa.challengeAndVerify({
      factorId: factor.id,
      code: mfaCode.trim(),
    })

    if (verificationError) throw verificationError
  }

  async function updatePassword(event: FormEvent) {
    event.preventDefault()
    setError(null)

    if (password.length < 8) {
      setError('Use at least 8 characters for your new password.')
      return
    }

    if (password !== confirmation) {
      setError('The two passwords do not match.')
      return
    }

    setBusy(true)

    try {
      await verifyMfaIfRequired()
      const { error: updateError } = await supabase.auth.updateUser({ password })
      if (updateError) throw updateError

      await supabase.auth.signOut()
      setComplete(true)
    } catch (recoveryError) {
      setError(recoveryError instanceof Error ? recoveryError.message : 'Password recovery could not be completed.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="login-page">
      <section className="login-panel">
        <BrandMark />
        <div className="login-panel__copy">
          <span className="eyebrow">One VenTuhi account</span>
          <h1>Choose a new password.</h1>
          <p>Your new password will unlock every VenTuhi workspace you are authorised to use.</p>
        </div>
      </section>

      <section className="login-card" aria-label="Choose a new password">
        <div>
          <span className="eyebrow">Account recovery</span>
          <h2>{complete ? 'Password updated' : 'Create your new password'}</h2>
          {!complete && mfaRequired && (
            <p>Because MFA is enabled, confirm the six-digit code from your authenticator app before changing the password.</p>
          )}
        </div>

        {complete ? (
          <div className="form-stack">
            <div className="message message--success">
              Your VenTuhi password has been changed for both Security and Compliance.
            </div>
            <a className="button button--primary button--large" href="/">
              Return to sign in
            </a>
          </div>
        ) : (
          <form className="form-stack" onSubmit={updatePassword}>
            {mfaRequired && (
              <label>
                Authenticator code
                <input
                  type="text"
                  value={mfaCode}
                  onChange={(event) => setMfaCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  required
                  disabled={!ready}
                />
              </label>
            )}
            <label>
              New password
              <input
                type={showPasswords ? 'text' : 'password'}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="new-password"
                minLength={8}
                required
                disabled={!ready}
              />
            </label>
            <label>
              Confirm new password
              <input
                type={showPasswords ? 'text' : 'password'}
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
                autoComplete="new-password"
                minLength={8}
                required
                disabled={!ready}
              />
            </label>
            <button className="text-link" type="button" onClick={() => setShowPasswords((visible) => !visible)}>
              {showPasswords ? 'Hide passwords' : 'Show passwords'}
            </button>
            {!ready && !error && <div className="message">Validating recovery link…</div>}
            {error && <div className="message message--error">{error}</div>}
            <button className="button button--primary button--large" disabled={!ready || busy}>
              {busy ? 'Securing your account…' : 'Update password'}
            </button>
          </form>
        )}
      </section>
    </main>
  )
}
